import ConfigurationRepository from "../domain/data/ConfigurationRepository";
import { ClassroomSizeConfig, FeederPatternConfig, FeederPatternImportResult, SchoolDirectoryBatchResult, SchoolDirectoryEntry, SeatLimitConfig, SiteConfigs } from "../domain/entities/Configs";
import AwsApiClient from "../presentation/hooks/AwsApiClient";

export default class APIPermissionRepository
  implements ConfigurationRepository {
  private client = new AwsApiClient();

  async getSiteConfigs(orgId: string, page: string): Promise<SiteConfigs[]> {
    try {
      const response = await this.client.get(
        `/api/config?orgId=${orgId}&page=${page}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveSiteConfigs(
    orgId: string,
    page: string,
    configJson: SiteConfigs
  ): Promise<SiteConfigs> {
    try {
      const response = await this.client.post(
        `/api/config?orgId=${orgId}&page=${page}`,
        configJson
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveImage(
    orgId: string,
    fileName: string,
    file: File
  ): Promise<boolean> {
    const arrayBuffer = await file.arrayBuffer();
    //const formdata = new FormData();
    //formdata.append("file", file, file.name);

    try {
      await this.client.postFile(
        `/api/config/image2?orgId=${orgId}&fileName=${fileName}`,
        arrayBuffer
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async getImage(orgId: string, fileName: string): Promise<File> {
    try {
      const response = await this.client.getExternal(
        `/api/config/image?orgId=${orgId}&fileName=${fileName}`,
        "blob"
      );
      return new File([response], fileName, { type: response.type });
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveSeatLimitConfig(seatLimitConfig: SeatLimitConfig): Promise<SeatLimitConfig> {
    try {
      const response = await this.client.post(
        `/api/config/seatlimit`,
        seatLimitConfig
      );

      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getSeatLimitConfigs(organizationId: string): Promise<SeatLimitConfig[]> {
    try {
      const response = await this.client.get(
        `/api/config/seatlimit/${organizationId}`
      );

      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveClassroomSizeConfig(classroomSizeConfig: ClassroomSizeConfig): Promise<ClassroomSizeConfig> {
    try {
      const response = await this.client.post(
        `/api/config/classroomSize`,
        classroomSizeConfig
      );

      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getClassroomSizeConfigs(organizationId: string): Promise<ClassroomSizeConfig[]> {
    try {
      const response = await this.client.get(
        `/api/config/classroomSize/${organizationId}`
      );

      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  // ── Feeder Pattern ─────────────────────────────────────

  async saveFeederPatternConfig(feederPattern: FeederPatternConfig): Promise<FeederPatternConfig> {
    try {
      const response = await this.client.post(
        `/api/config/feederPattern`,
        feederPattern
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getFeederPatternConfigs(organizationId: string): Promise<FeederPatternConfig[]> {
    try {
      const response = await this.client.get(
        `/api/config/feederPattern/${organizationId}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async deleteFeederPatternConfig(organizationId: string, feederPatternId: string): Promise<void> {
    try {
      await this.client.delete(
        `/api/config/feederPattern/${organizationId}/${feederPatternId}`
      );
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async importFeederPatternConfigs(organizationId: string, configs: FeederPatternConfig[]): Promise<FeederPatternImportResult> {
    try {
      const response = await this.client.post(
        `/api/config/feederPattern/${organizationId}/import`,
        configs
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  // ── School Directory ─────────────────────────────────────

  async getSchoolDirectory(organizationId: string): Promise<SchoolDirectoryEntry[]> {
    try {
      return await this.client.get(`/api/config/school-directory/${organizationId}`);
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }

  async saveSchoolDirectoryEntry(organizationId: string, entry: SchoolDirectoryEntry): Promise<SchoolDirectoryEntry> {
    try {
      return await this.client.post(`/api/config/school-directory/${organizationId}`, entry);
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }

  async saveSchoolDirectoryBatch(organizationId: string, entries: SchoolDirectoryEntry[]): Promise<SchoolDirectoryBatchResult> {
    try {
      return await this.client.post(`/api/config/school-directory/${organizationId}/batch`, entries);
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }

  async deleteSchoolDirectoryEntry(organizationId: string, schoolId: string): Promise<void> {
    try {
      await this.client.delete(`/api/config/school-directory/${organizationId}/${schoolId}`);
    } catch (error) {
      throw new Error(`An error has occurred: ${error}`);
    }
  }
}
